import { useState, useEffect } from "react";

import { TextField, DatePicker, Checkbox, Select, Typography, DocumentList, Button, DocumentListItem, Modal } from "@king-ict/eupisi-ui/components";
import { useValidation } from "@king-ict/eupisi-ui/hooks";
import { CodeBook, DocumentData } from "@king-ict/eupisi-ui/models";
import { codeBookToSelectData, nameof } from "@king-ict/eupisi-ui/utils";
import { saveAs } from "file-saver";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { useParams } from "react-router";

import { downloadDocument, DocumentUploadRequest, uploadDocument, createNotification as createNotificationApi, editNotification, getNotification } from "../../api/nisooApi";
import { DocumentsUploadModal } from "../../components/DocumentsUploadModal";
import { TextEditorV2 } from "../../components/TextEditorV2";
import { useInput } from "../../hooks";
import { DocumentType } from "../../models/Document";
import { Notification, createNotification, NotificationRequest, createNotificationRequestFromNotification, NotificationValidationScheme } from "../../models/Notification";
import { RoleEnum } from "../../models/User/RoleEnum";
import { useGetNotificationRolesQuery, useGetUserRolesQuery } from "../../queries/codeBookQuery"
import { useGetUserSessionQuery } from "../../queries/userQuery";
import { notificationAdministrationRoute, notificationDetailUrl } from "../../routes/routes";
import styles from "./NotificationList.module.scss";


export function NotificationEdit() {

    const { t } = useTranslation();
    const history = useHistory();
    // const {data: notificationRoles} = useGetNotificationRolesQuery();
    const { data: notificationRoles } = useGetUserRolesQuery();
    const { data: userSession } = useGetUserSessionQuery();
    const params = useParams<{ notificationId: string }>();
    const { handleChange } = useInput();

    const [notification, setNotification] = useState<Notification>(createNotification());
    const [openUploadAttachmentsModal, setOpenUploadAttachmentsModal] = useState(false);
    const [openCancelModal, setOpenCancelModal] = useState(false);
    const [openConfirmModal, setOpenConfirmModal] = useState(false);
    const [openSuccessModal, setOpenSuccessModal] = useState(false);

    const [errors, doValidations] = useValidation({
        data: notification,
        validationSchema: NotificationValidationScheme
    });

    useEffect(() => {
        if (!userSession || (userSession.roleId !== RoleEnum.ASOOAdmin && userSession.roleId !== RoleEnum.ASOOManager && userSession.roleId !== RoleEnum.ASOOOffice
            && userSession.roleId !== RoleEnum.CASAdmin && userSession.roleId !== RoleEnum.CASManager && userSession.roleId !== RoleEnum.CASOffice
            && userSession.roleId !== RoleEnum.Helpdesk)) {
            return;
        }

        const fetchNotification = async () => {
            if (!params.notificationId || isNaN(parseInt(params.notificationId))) {
                return;
            }
            var result = await getNotification(parseInt(params.notificationId));
            setNotification(result);
        }

        fetchNotification();

    }, [params.notificationId, userSession]);


    const handleNotificationMessageChange = (newMessage: string) => {        
        const newNotification: Notification = {
            ...notification,
            message: newMessage
        };
        setNotification(newNotification);
    };

    const handleAttachmentRemove = (index: number) => {
        const newAttachments: DocumentData[] = [...notification.attachments];

        if (index !== -1) {
            newAttachments.splice(index, 1);
            setNotification((prev) => ({
                ...prev,
                attachments: newAttachments
            }));
        }
    };

    const handleAttachmentsConfirm = (documents: DocumentData[]) => {
        if (documents && documents.length > 0) {
            const newAttachments = notification.attachments ? notification.attachments.slice() : [];
            newAttachments.push(...documents);

            const newNotification: Notification = {
                ...notification,
                attachments: newAttachments
            };

            setNotification(newNotification);
        }

        setOpenUploadAttachmentsModal(false);
    };

    const handleAttachmentDownload = async (document: DocumentData) => {
        if (document.id === undefined) {
            return;
        }
        if (document.id === -1) {
            saveAs(document.file!, document.fileName);
        }
        else {
            await downloadDocument(DocumentType.NotificationAttachment, document.id);
        }
    };

    const handleSaveClick = () => {

        if (!doValidations()) {
            return;
        }
        setOpenConfirmModal(true);
    };

    const handleCancelClick = () => {
        setOpenCancelModal(true);
    };

    const handleCancelCancelModal = () => {
        setOpenCancelModal(false);
    };

    const handleConfirmCancelModal = () => {
        setOpenCancelModal(false);

        if (!params.notificationId || isNaN(parseInt(params.notificationId))) {
            history.push(notificationAdministrationRoute());
        }
        else {
            history.push(notificationDetailUrl(parseInt(params.notificationId)));
        }

        // history.push(notificationAdministrationRoute());
    };

    const handleCancelConfirmModal = () => {
        setOpenConfirmModal(false);
    };

    const handleConfirmConfirmModal = async () => {
        setOpenConfirmModal(false);

        //uploadaj dokument
        await uploadAttachments();
        //mapiranje
        const request = createNotificationRequestFromNotification(notification);

        if (request.id === 0) {
            createNotificationApi(request).then(() => {
                setOpenSuccessModal(true);
            });
        }
        else {
            editNotification(request).then(() => {
                setOpenSuccessModal(true);
            });
        }
    };

    const handleConfirmSuccessModal = () => {
        setOpenSuccessModal(false);
        history.push(notificationAdministrationRoute());
    };
        
    const createDocumentRequest = (documentFile: File, documentTypeID: DocumentType): DocumentUploadRequest => {
        return {
            document: documentFile,
            documentTypeId: documentTypeID,    
        };
    }

    const uploadAttachments = async () => {
        const promises = [] as Promise<void>[];

        if (notification.attachments && notification.attachments.length > 0) {
            notification.attachments.map((document, index) => {
                if (document && document.id === -1) {                    
                    const attachmentRequest = createDocumentRequest(document.file!, DocumentType.NotificationAttachment);
                    const attachmentPromise = uploadDocument(attachmentRequest).then((idDoc) => {
                        var attachments = notification.attachments.slice();
                        if (attachments && attachments.length > 0) {
                            attachments[index]!.id = idDoc;
                            setNotification((prev) => ({
                                ...prev,
                                attachments: attachments
                            }));
                        }
                    });
                    promises.push(attachmentPromise);
                }
            });
            await Promise.all(promises);
        }
    };

    if (params.notificationId && !isNaN(parseInt(params.notificationId)) && !notification.message) {
        return null;
    }
    return (
        <>
            <Row>
                <Col md={12}>
                    <h2>{t("common.notification")}</h2>
                </Col>
            </Row>
            <div className="mt-4 pr-5 mr-5">
                <div className={styles.panel}>
                    <Row>
                        <Col md={12}>
                            <TextField
                                label={t("notification.title")}
                                name={nameof<typeof notification>("title")}
                                value={notification.title}
                                onChange={handleChange(setNotification)}
                                errorText={errors?.title}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12}>
                            <TextEditorV2
                                label={t("notification.message")}
                                handleChange={handleNotificationMessageChange}
                                initialValue={notification.message}
                                errorText={errors?.message}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={5}>
                            <DatePicker
                                label={t("notification.validFrom")}
                                name={nameof<typeof notification>("validFrom")}
                                value={notification.validFrom}
                                onChange={handleChange(setNotification)}
                                showTimePicker={true}
                                className="mt-2"
                                errorText={errors?.validFrom}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={4}>
                            <Checkbox
                                label={t("notification.putOnTop")}
                                name={nameof<typeof notification>("putOnTop")}
                                checked={notification.putOnTop}
                                onChange={handleChange(setNotification)}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12}>
                            <Select
                                multiple
                                name={nameof<typeof notification>("roles")}
                                label={t("notification.intendedForRoles")}
                                value={notification.roles}
                                onChange={handleChange(setNotification)}
                                selectData={codeBookToSelectData(notificationRoles)}
                                errorText={errors?.roles?.toString()}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12}>
                            <Checkbox
                                label={t("notification.isActive")}
                                name={nameof<typeof notification>("isActive")}
                                checked={notification.isActive}
                                onChange={handleChange(setNotification)}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12}>
                            <Typography variant="medium" component="div" color="light">
                                {t("notification.attachments")}
                            </Typography>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={4}>
                            {notification.attachments && notification.attachments.map((document, index) => (
                                <DocumentListItem
                                    key={index}
                                    document={document}
                                    onRemoveClick={() => handleAttachmentRemove(index)}
                                    onDocumentClick={(doc) => handleAttachmentDownload(doc)}
                                />
                            ))}
                        </Col>
                    </Row>
                    <Row>
                        <Col md={4}>
                            <Button
                                color="primary"
                                onClick={() => setOpenUploadAttachmentsModal(true)}
                                size="sm">
                                {t("notification.addAttachments")}
                            </Button>
                        </Col>
                    </Row>

                </div>
            </div>
            <Row>
                <Col md={11}>
                    <Button
                        className="mr-1 float-right"
                        onClick={handleSaveClick}
                    >
                        {t("common.save")}
                    </Button>
                    <Button
                        outline
                        className="mr-1 float-right"
                        onClick={handleCancelClick}
                    >
                        {t("common.cancel")}
                    </Button>

                </Col>
            </Row>
            <DocumentsUploadModal
                title={t("common.documentModalTitle")}
                infoPanelText={t("common.documentModalInfo")}
                types={t("document.imagesAndPdfAndWordFilter")}
                allDocuments={notification.attachments}
                onRemoveUploadedDocument={handleAttachmentRemove}
                maxSize={10}
                maxFiles={notification.attachments ? 2 - notification.attachments.length : 2}
                filesAction={undefined}
                open={openUploadAttachmentsModal}
                onConfirm={handleAttachmentsConfirm}
                onClose={() => setOpenUploadAttachmentsModal(false)}
                hideOldDocuments={false}
                btnCancelText={t("common.cancel")}
                btnOkText={t("common.addNew")}
            />
            <Modal
                type="confirm"
                open={openCancelModal}
                cancelText={t("common.noProperCase")}
                confirmText={t("common.yesCancel")}
                onClose={handleCancelCancelModal}
                onConfirm={handleConfirmCancelModal}
            >
                <div>
                    <Typography component="h1" variant="largeBold">
                        {t("common.areYouSureCancelChanges")}
                    </Typography>
                    {t("common.areYouSureCancelChangesNote")}
                </div>
            </Modal>
            <Modal
                type="confirm"
                open={openConfirmModal}
                confirmText={t("common.yesSaveChanges")}
                cancelText={t("common.cancel")}
                onClose={handleCancelConfirmModal}
                onConfirm={handleConfirmConfirmModal}
            >
                <div>
                    <Typography component="h1" variant="largeBold">
                        {t("notification.areYouSureNotificationSave")}
                    </Typography>
                </div>
            </Modal>
            <Modal
                type="message"
                open={openSuccessModal}
                confirmText={t("common.close")}
                onClose={handleConfirmSuccessModal}
                onConfirm={handleConfirmSuccessModal}
            >
                <div className={styles.modalMessageContentAlignCenter}>
                    <Typography component="h1" variant="largeBold">
                        {t("notification.notificationSuccessfullySubmitted")}
                    </Typography>
                </div>
            </Modal>
        </>
    );
};